﻿import { useEffect, useMemo, useState } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, TextField,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, List, Divider, Box, Pagination, InlineGrid,
} from '@shopify/polaris';
import { ChartPopularIcon, LightbulbIcon } from '@shopify/polaris-icons';
import { useCompetitors } from '../../hooks/useCompetitors';
import type { CompetitorAnalysis, ActionItem } from '../../api/competitors';
import { StatCard } from '../shared/StatCard';

function priorityBadge(priority: string) {
  const map: Record<string, 'critical' | 'warning' | 'info'> = {
    high: 'critical',
    medium: 'warning',
    low: 'info',
  };
  return <Badge tone={map[priority] ?? 'info'}>{priority}</Badge>;
}

function Section({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <BlockStack gap="200">
      <Text as="h3" variant="headingSm">{title}</Text>
      <List type="bullet">
        {items.map((item, i) => <List.Item key={i}>{item}</List.Item>)}
      </List>
    </BlockStack>
  );
}

interface AnalysisDetailProps {
  analysis: CompetitorAnalysis;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function AnalysisDetail({ analysis, onBack, onDelete }: AnalysisDetailProps) {
  return (
    <Page fullWidth
      title={`Analysis: ${analysis.competitor_name}`}
      subtitle={analysis.competitor_niche ?? undefined}
      backAction={{ content: 'All Analyses', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(analysis.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          {analysis.summary && (
            <Card>
              <Text as="p" variant="bodyMd">{analysis.summary}</Text>
            </Card>
          )}

          <Card>
            <BlockStack gap="500">
              <InlineStack gap="600" wrap>
                <Box minWidth="45%">
                  <Section title="Strengths" items={analysis.competitor_strengths} />
                </Box>
                <Box minWidth="45%">
                  <Section title="Weaknesses" items={analysis.competitor_weaknesses} />
                </Box>
              </InlineStack>

              <Divider />

              <InlineStack gap="600" wrap>
                <Box minWidth="45%">
                  <Section title="Opportunities for Us" items={analysis.opportunities} />
                </Box>
                <Box minWidth="45%">
                  <Section title="Threats" items={analysis.threats} />
                </Box>
              </InlineStack>

              <Divider />

              <Section title="Positioning Gaps" items={analysis.positioning_gaps} />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Action Items</Text>
              <DataTable
                columnContentTypes={['text', 'text', 'text']}
                headings={['Priority', 'Action', 'Rationale']}
                rows={analysis.action_items.map((a: ActionItem) => [
                  priorityBadge(a.priority),
                  a.action,
                  a.rationale,
                ])}
              />
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

interface FormState {
  name: string;
  url: string;
  niche: string;
  notes: string;
}

const emptyForm: FormState = { name: '', url: '', niche: '', notes: '' };

interface Props {
  onBack?: () => void;
}

export function CompetitorAnalysisPanel(_props: Props) {
  const {
    analyses, activeAnalysis, loading, analyzing, error,
    total, page, lastPage, fetchAnalyses, analyze, deleteAnalysis, setActiveAnalysis, setError,
  } = useCompetitors();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchAnalyses(1);
  }, []);

  const totalOpportunities = useMemo(
    () => analyses.reduce((sum, a) => sum + a.opportunities.length, 0),
    [analyses],
  );

  if (activeAnalysis) {
    return (
      <AnalysisDetail
        analysis={activeAnalysis}
        onBack={() => setActiveAnalysis(null)}
        onDelete={deleteAnalysis}
      />
    );
  }

  const handleAnalyze = async () => {
    if (!form.name.trim()) return;
    const result = await analyze({
      name: form.name,
      url: form.url || undefined,
      niche: form.niche || undefined,
      notes: form.notes || undefined,
    });
    if (result) {
      setForm(emptyForm);
      setShowForm(false);
    }
  };

  return (
    <Page fullWidth
      title="Competitor Analysis"
      subtitle="AI-powered competitive intelligence for your Shopify store"
      primaryAction={{
        content: 'Analyze Competitor',
        onAction: () => setShowForm(true),
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <StatCard label="Saved Analyses" value={String(total)} icon={ChartPopularIcon} iconColor="purple" />
            <StatCard label="Opportunities Identified" value={String(totalOpportunities)} icon={LightbulbIcon} iconColor="orange" />
          </InlineGrid>

          {showForm && (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">New Competitor Analysis</Text>
                <TextField
                  label="Competitor Name *"
                  value={form.name}
                  onChange={(v) => setForm((f) => ({ ...f, name: v }))}
                  placeholder="e.g., AceStore"
                  autoComplete="off"
                />
                <TextField
                  label="Competitor URL"
                  value={form.url}
                  onChange={(v) => setForm((f) => ({ ...f, url: v }))}
                  placeholder="https://competitor.com"
                  autoComplete="off"
                />
                <TextField
                  label="Niche / Category"
                  value={form.niche}
                  onChange={(v) => setForm((f) => ({ ...f, niche: v }))}
                  placeholder="e.g., Fitness Equipment"
                  autoComplete="off"
                />
                <TextField
                  label="Notes (what you know about them)"
                  value={form.notes}
                  onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                  placeholder="They have a strong Instagram presence, focus on premium pricing..."
                  multiline={3}
                  autoComplete="off"
                />
                <InlineStack gap="300">
                  <Button
                    variant="primary"
                    loading={analyzing}
                    disabled={!form.name.trim()}
                    onClick={handleAnalyze}
                  >
                    Run Analysis
                  </Button>
                  <Button onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                    Cancel
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Saved Analyses ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : analyses.length === 0 ? (
                <EmptyState
                  heading="No competitor analyses yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Analyze First Competitor',
                    onAction: () => setShowForm(true),
                  }}
                >
                  <p>Enter a competitor's details and let AI analyse their positioning, strengths, and the opportunities they leave open for you.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Competitor', 'Niche', 'Opportunities', 'Actions']}
                  rows={analyses.map((a) => [
                    <Button variant="plain" onClick={() => setActiveAnalysis(a)}>{a.competitor_name}</Button>,
                    a.competitor_niche ?? '—',
                    `${a.opportunities.length} identified`,
                    <Button
                      variant="plain"
                      tone="critical"
                      size="slim"
                      onClick={() => deleteAnalysis(a.id)}
                    >
                      Delete
                    </Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchAnalyses(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchAnalyses(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
